import type { PassengerAnswers, QuestionBlock, QuestionnaireContext, ReroutingMode } from "./types";

/**
 * The screens of the questionnaire, one question each. Their order here is the order shown.
 *
 * Only some are still asked (`buildQuestionnaireScreens`); the others stay so that answers already
 * given keep their recap and their admin display.
 */
export const QUESTIONNAIRE_SCREENS = [
  "VERIFICATION",
  "CORRECTION",
  "NOT_TRAVELLED",
  "PRE_EXISTING_CLAIM",
  "REROUTING",
  "REROUTING_MEANS",
  "ARRIVAL",
  "INFORMATION_TIMING",
  "INFORMATION_CHANNEL",
  "PRIOR_STEPS",
  "PRIOR_STEPS_DETAILS",
  "CO_PASSENGERS",
  "EXPENSES",
  "AIRLINE_REASON",
  "CONSEQUENCES",
  "OTHER_ISSUES",
  "PRECISIONS",
  "RECAP",
] as const;
export type QuestionnaireScreen = (typeof QUESTIONNAIRE_SCREENS)[number];

/** Screens answered by a single tap: choosing moves on by itself. */
export const SINGLE_CHOICE_SCREENS: readonly QuestionnaireScreen[] = [
  "VERIFICATION",
  "NOT_TRAVELLED",
  "PRE_EXISTING_CLAIM",
  "REROUTING",
  "REROUTING_MEANS",
  "ARRIVAL",
  "INFORMATION_TIMING",
  "INFORMATION_CHANNEL",
  "EXPENSES",
  "AIRLINE_REASON",
];

/** Screens that can be left empty. */
export const OPTIONAL_SCREENS: readonly QuestionnaireScreen[] = [
  "PRIOR_STEPS_DETAILS",
  "CO_PASSENGERS",
  "AIRLINE_REASON",
  "CONSEQUENCES",
  "OTHER_ISSUES",
  "PRECISIONS",
];

/** No other flight from the airline: how the passenger got there instead (« Non », then one of these). */
export const SELF_REROUTING_MODES: readonly ReroutingMode[] = ["SELF_BOOKED", "OTHER_TRANSPORT", "ABANDONED"];

/**
 * The passenger says the incident did not happen to them: no signature, an admin looks at the file.
 */
export function requiresAdminReview(answers: Partial<PassengerAnswers>): boolean {
  const verification = answers.verification;

  return (
    verification?.status === "NOT_TRAVELLED" ||
    (verification?.status === "CORRECTED" && Boolean(verification.corrections?.includes("NO_INCIDENT")))
  );
}

/** « Avez-vous déjà réclamé ? », or what the former « démarches » question implied. */
export function hasPreExistingClaim(answers: Partial<PassengerAnswers>): boolean {
  if (typeof answers.preExistingClaim === "boolean") {
    return answers.preExistingClaim;
  }

  const steps = answers.priorSteps?.steps ?? [];

  return steps.some(
    (step) => step === "CLAIM_PENDING" || step === "COMPENSATED" || step === "VOUCHER_ACCEPTED",
  );
}

/**
 * The path through the questionnaire for the answers given so far: recomputed after every answer,
 * so the progress and the « suivant » follow the branches.
 *
 * Short on purpose: pressing « Confirmer l'incident » confirms the flight shown on the page, so
 * the incident is not asked again (`verification` is sent as `CONFIRMED`). The free note stays on
 * the page, above the button (`precisions`).
 */
export function buildQuestionnaireScreens(
  context: QuestionnaireContext,
  answers: Partial<PassengerAnswers>,
): QuestionnaireScreen[] {
  const has = (block: QuestionBlock) => context.blocks.includes(block);
  // Always asked: the modal's former last step.
  const screens: QuestionnaireScreen[] = ["PRE_EXISTING_CLAIM"];
  const mode = answers.rerouting?.mode;

  if (has("REROUTING")) {
    screens.push("REROUTING");

    if (mode === "AIRLINE") {
      screens.push("ARRIVAL");
    } else if (mode) {
      screens.push("REROUTING_MEANS");
    }
  }

  if (has("AIRLINE_REASON")) {
    screens.push("AIRLINE_REASON");
  }

  // A single question needs no recap: it is confirmed from where it is answered.
  return screens.length > 1 ? [...screens, "RECAP"] : screens;
}

/** Whether a screen has what it needs to move on (optional screens always do). */
export function isScreenAnswered(screen: QuestionnaireScreen, answers: Partial<PassengerAnswers>): boolean {
  if (OPTIONAL_SCREENS.includes(screen)) {
    if (screen === "CO_PASSENGERS") {
      return (answers.coPassengers ?? []).every(
        (passenger) => passenger.status !== "EMAIL" || looksLikeEmail(passenger.email ?? ""),
      );
    }

    return true;
  }

  switch (screen) {
    case "VERIFICATION":
      return Boolean(answers.verification?.status);
    case "CORRECTION":
      return Boolean(answers.verification?.corrections?.length);
    case "NOT_TRAVELLED":
      return Boolean(answers.verification?.notTravelledReason);
    case "PRE_EXISTING_CLAIM":
      return typeof answers.preExistingClaim === "boolean";
    case "REROUTING":
      return Boolean(answers.rerouting?.mode);
    case "REROUTING_MEANS":
      return Boolean(answers.rerouting && SELF_REROUTING_MODES.includes(answers.rerouting.mode));
    case "ARRIVAL":
      return Boolean(answers.rerouting?.arrivalDelay);
    case "INFORMATION_TIMING":
      return Boolean(answers.passengerInformation?.timing);
    case "INFORMATION_CHANNEL":
      return Boolean(answers.passengerInformation?.channel);
    case "PRIOR_STEPS":
      return Boolean(answers.priorSteps?.steps.length);
    case "EXPENSES":
      return typeof answers.expenses?.hasExpenses === "boolean";
    default:
      return true;
  }
}

/**
 * Drop the answers of the screens no longer on the path: going back and changing a branch must not
 * leave a stale « je n'ai pas pris ce vol » reason behind a confirmed incident.
 */
export function pruneAnswers(context: QuestionnaireContext, answers: PassengerAnswers): PassengerAnswers {
  const screens = new Set(buildQuestionnaireScreens(context, answers));
  const pruned: PassengerAnswers = {
    version: answers.version,
    locale: answers.locale,
    verification: { status: answers.verification.status },
  };

  if (screens.has("CORRECTION")) {
    pruned.verification.corrections = answers.verification.corrections ?? [];
  }

  if (screens.has("NOT_TRAVELLED")) {
    pruned.verification.notTravelledReason = answers.verification.notTravelledReason ?? null;
  }

  if ((screens.has("CORRECTION") || screens.has("NOT_TRAVELLED")) && answers.verification.details?.trim()) {
    pruned.verification.details = answers.verification.details.trim();
  }

  if (screens.has("PRE_EXISTING_CLAIM") && typeof answers.preExistingClaim === "boolean") {
    pruned.preExistingClaim = answers.preExistingClaim;
  }

  if (screens.has("REROUTING") && answers.rerouting) {
    pruned.rerouting = screens.has("ARRIVAL")
      ? {
          mode: answers.rerouting.mode,
          arrivalDelay: answers.rerouting.arrivalDelay ?? null,
          flightNumber: answers.rerouting.flightNumber?.trim() || null,
        }
      : { mode: answers.rerouting.mode };
  }

  if (screens.has("INFORMATION_TIMING") && answers.passengerInformation) {
    pruned.passengerInformation = {
      timing: answers.passengerInformation.timing,
      channel: screens.has("INFORMATION_CHANNEL") ? (answers.passengerInformation.channel ?? null) : null,
    };
  }

  if (screens.has("PRIOR_STEPS") && answers.priorSteps) {
    const { steps } = answers.priorSteps;
    pruned.priorSteps = {
      steps,
      claimReference: steps.includes("CLAIM_PENDING")
        ? answers.priorSteps.claimReference?.trim() || null
        : null,
      compensationAmount: steps.includes("COMPENSATED")
        ? (answers.priorSteps.compensationAmount ?? null)
        : null,
    };
  }

  if (answers.coPassengers?.length) {
    pruned.coPassengers = answers.coPassengers.map((passenger) => ({
      ...passenger,
      email: passenger.status === "EMAIL" ? passenger.email?.trim() || null : null,
    }));
  }

  if (screens.has("EXPENSES") && answers.expenses) {
    pruned.expenses = answers.expenses;
  }

  if (screens.has("AIRLINE_REASON") && answers.airlineReason) {
    pruned.airlineReason = answers.airlineReason;

    if (answers.airlineReason === "OTHER" && answers.airlineReasonDetails?.trim()) {
      pruned.airlineReasonDetails = answers.airlineReasonDetails.trim();
    }
  }

  if (screens.has("CONSEQUENCES") && answers.consequences?.length) {
    pruned.consequences = answers.consequences;
  }

  if (screens.has("OTHER_ISSUES") && answers.otherIssues?.length) {
    pruned.otherIssues = answers.otherIssues;
  }

  // The note typed on the page. The admin's own question is not shown with the questionnaire.
  if (answers.precisions?.text?.trim()) {
    pruned.precisions = { question: null, text: answers.precisions.text.trim() };
  }

  return pruned;
}

/** Deliberately permissive: it decides a red border, not whether an address exists. */
export function looksLikeEmail(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(value.trim());
}

/** « FR76 3000 6000 0112 3456 7890 189 » and the like, spaced or not. */
const IBAN_PATTERN = /\b[A-Z]{2}\d{2}(?:[ -]?[A-Z0-9]{4}){2,7}(?:[ -]?[A-Z0-9]{1,4})?\b/gi;

/** The passenger was asked not to: warn while typing. */
export function containsBankDetails(text: string): boolean {
  IBAN_PATTERN.lastIndex = 0;
  const found = IBAN_PATTERN.test(text) || /\b(iban|rib|bic)\b/i.test(text);
  IBAN_PATTERN.lastIndex = 0;

  return found;
}

/** …and mask what got through anyway before it reaches documents, search and Slack. */
export function redactBankDetails(text: string): string {
  return text.replace(IBAN_PATTERN, "[IBAN masqué]");
}
